// TYPE ELITE — Polished prompt compiler v3.
// Produces prose-style, AI-ready prompts organized by section. Product-aware composition.
// Fill Mode governs solid vs gradient vs pattern/material output.
import { PALETTES, NEGATIVE_INSTRUCTIONS_DEFAULT } from '../data/optionsLibrary';

const isTransparent = (mode) => mode && mode.startsWith('transparent-');

function block(title, body) {
  if (!body) return '';
  return `${title}\n${body}`;
}
function bullet(label, val) {
  if (val === undefined || val === null) return '';
  if (Array.isArray(val)) { if (!val.length) return ''; return `• ${label}: ${val.join(', ')}`; }
  const s = String(val).trim(); if (!s) return '';
  return `• ${label}: ${s}`;
}

function palettePretty(name) {
  if (!name) return '';
  const sw = PALETTES[name];
  return sw ? `${name} (${sw.join(', ')})` : name;
}

function gradientDescription(g) {
  if (!g || !g.stops || g.stops.length < 2) return '';
  const stops = g.stops.map(s => `${s.color}${s.opacity != null && s.opacity !== 100 ? `@${s.opacity}%op` : ''} at ${Math.round(s.pos)}%`).join(', ');
  return `${g.type || 'Linear'} gradient @ ${g.angle ?? 90}° — stops: ${stops}`;
}

const PRODUCT_BRIEFS = {
  'tshirt-dtf': 'This is a T-shirt / DTF graphic. Compose as one centered, self-contained artwork with generous transparent padding, print-ready color separation and no garment or mockup visible.',
  'hoodie-graphic': 'This is a hoodie graphic. Compose as a bold chest print or hood-friendly layout with strong contrast for cotton fleece.',
  'hat-cap': 'This is a hat / cap design. Keep the artwork compact for the front panel or curved brim; avoid tiny details that will not embroider or print cleanly.',
  'tote-bag': 'This is a tote bag design. Compose for a rectangular canvas panel; keep artwork bold and readable at a distance.',
  'tumbler-sublimation': 'This is a tumbler / sublimation wrap. Compose so the artwork flows edge-to-edge around a cylinder; keep hero elements away from the top and bottom bleeds and expect stretch at the seam.',
  'mug-design': 'This is a mug wrap. Compose horizontally with the hero centered; leave breathing room where the handle meets the design.',
  'phone-case': 'This is a phone case design. Compose vertically with the hero above the camera cutout; keep 10% safe margin on all edges.',
  'sticker': 'This is a sticker / decal design. Build one bold shape with a die-cut friendly outline and no background.',
  'sticker-sheet': 'This is a sticker sheet. Arrange multiple related pieces on one canvas with clear spacing so each can be cut individually.',
  'vinyl-decal': 'This is a vinyl decal. Keep artwork to one or two spot colors with a clean silhouette that can be cut on vinyl.',
  'neon-sign': 'This is a neon sign design. Build the typography as glowing neon tubes on a dark background with soft ambient glow bleed.',
  'wall-art': 'This is a wall-art poster. Compose for a portrait canvas with editorial hierarchy and safe printing margins.',
  'coloring-page': 'This is a coloring page. Deliver clean black line art on pure white with no fill.',
  'tattoo-flash': 'This is a tattoo flash sheet. Render bold ink linework with limited or no shading and no color unless requested.',
  'book-cover': 'This is a book / album cover. Compose editorial hierarchy with hero title and clear subtitle space.',
  'podcast-cover': 'This is a podcast / YouTube cover. Compose square, hero-first, thumbnail-legible at small size.',
  'youtube-thumbnail': 'This is a YouTube thumbnail. Compose 16:9 with a bold expressive hero, high-contrast type, minimal noise.',
  'business-flyer': 'This is a business flyer. Compose full-hierarchy: hero headline, subheadline, offer block, contact block, negative space.',
  'event-flyer': 'This is an event flyer. Compose full-hierarchy: event name, date/time, venue, headliner and call to action, cinematic mood.',
  'wedding-invite': 'This is a wedding invitation. Compose elegant editorial hierarchy with names, date, venue, RSVP block, refined negative space.',
  'greeting-card': 'This is a greeting card. Compose front-panel hero art with expressive typography and mood setting.',
  'business-card': 'This is a business card. Compose compact hierarchy: name, title, contact block, brand mark, quiet margins.',
  'menu-price-list': 'This is a menu / price list. Compose organized rows with clear headers, price alignment and readable body copy.',
  'social-post': 'This is a social post. Compose thumbnail-first with hero text scaled for feed legibility.',
  'phone-wallpaper': 'This is a phone wallpaper. Compose vertically, keep hero away from the top notch and bottom gesture bar.',
  'product-ad': 'This is a product ad. Compose product hero with clear offer, urgency and call to action.',
  'poster': 'This is a poster. Compose cinematic hierarchy with strong hero and supporting information zones.',
  'banner': 'This is a web / banner header. Compose horizontally with hero centered or left-aligned.',
  'billboard': 'This is a billboard. Compose massive-scale readability with 3-word hero maximum.',
  'logo-lockup': 'This is a logo lockup. Compose one balanced mark with brand name, optional tagline and refined negative space, transparent background.',
  'monogram': 'This is a monogram. Compose centered initials as one refined mark, transparent background.',
  'sports-school': 'This is a sports / school design. Compose bold varsity hierarchy with team spirit props and readable jersey-friendly type.',
  'memorial': 'This is a memorial tribute. Compose reverent hierarchy with name, dates, portrait framing, soft lighting.',
  'holiday-seasonal': 'This is a holiday / seasonal design. Compose festive hierarchy with topical color palette and decorative props.',
  'fan-art': 'This is fan art / character-inspired. Compose expressive hero character with typography as support (never claim official likeness).',
  'trading-card': 'This is a trading card. Compose portrait with framing, stat blocks and holo finish where appropriate.',
  'sign-poster': 'This is a store / yard sign. Compose bold, readable-from-distance hierarchy with weather-friendly contrast.',
  'transparent-png': 'This is a transparent PNG graphic. Compose as one isolated design with a true alpha channel; no background, no mockup.',
  'character-collage': 'This is a character / cast collage. Compose multiple characters around the hero typography with balanced spacing.',
  'standalone-typography': 'This is a standalone typography treatment. Compose the exact text as the hero with supporting elements playing a decorative role.',
};

// Convert numeric spacing value into natural-language directive.
function spacingLanguage(value, dimension) {
  if (!value) return '';
  const abs = Math.abs(value);
  const bucket = abs < 4 ? 'slightly' : abs < 12 ? 'notably' : 'dramatically';
  const dir = value < 0 ? 'tighter' : 'wider';
  const word = { tracking: 'letter tracking', wordSpacing: 'word spacing', lineSpacing: 'line spacing', stackGap: 'stack gap' }[dimension] || dimension;
  return `${bucket} ${dir} ${word}`;
}

function buildFillDescription(p, targetName, fillEntry) {
  // fillEntry: { mode, color, opacity, gradient, paletteName }
  const parts = [];
  const mode = fillEntry.mode || p.fillMode || 'Solid Color';
  if (mode === 'Solid Color' && fillEntry.color) {
    parts.push(`solid ${fillEntry.color}${fillEntry.opacity && fillEntry.opacity !== 100 ? ` at ${fillEntry.opacity}% opacity` : ''}`);
  } else if ((mode === 'Custom Gradient' || mode === 'Preset Gradient') && fillEntry.gradient) {
    parts.push(gradientDescription(fillEntry.gradient));
  } else if (mode === 'Pattern / Material') {
    parts.push('driven by the pattern/material selections below');
  }
  return `• ${targetName}: ${parts.join(' ')}`;
}

export function compileMasterPrompt(p) {
  const out = [];

  // A. Opening lock — exact text FIRST
  out.push(`RENDER THIS EXACT TEXT AS THE HERO: "${p.exactText || ''}"`);
  out.push('The typography must remain the visual focus. Preserve every character, spacing and line break exactly.');
  out.push('');

  // B. Creative brief
  const brief = [
    bullet('Creation Type', p.creationTypeLabel || p.creationType),
    bullet('Purpose / Use', p.purpose),
    bullet('Topic / World', [p.topicCategoryLabel, p.topicItem, p.topicCustom].filter(Boolean).join(' — ')),
    p.customConcept && `• Additional Concept: ${p.customConcept}`,
  ].filter(Boolean).join('\n');
  out.push(block('=== CREATIVE BRIEF ===', brief || '• Standalone typography treatment · no additional brief specified'), '');

  const productNote = PRODUCT_BRIEFS[p.creationType];
  if (productNote) out.push(block('=== PRODUCT-READY COMPOSITION ===', productNote), '');

  // C. Typography
  const typo = [
    bullet('Font Family', p.fontFamilyLabel || p.fontFamily),
    bullet('Font Direction', p.fontStyle),
    bullet('Named Font Reference', p.namedFont),
    bullet('Style Modifiers', p.fontStyleModifiers),
    bullet('Letter Construction', p.letterConstruction),
    bullet('Layout', [p.layoutBasic, p.layoutStack, p.layoutArch, p.layoutPath, p.layoutShape, p.layoutPerspective, p.layoutDistortion].filter(Boolean).join(' + ')),
    bullet('Capitalization', p.capitalization),
    bullet('Styling Scope', p.applyScope),
  ].filter(Boolean).join('\n');
  if (typo) out.push(block('=== TYPOGRAPHY ===', typo), '');

  // C1. Creative & character-inspired reference (name + visual description + trademark-safe fallback)
  if (p.creativeFont && p.creativeFont.name) {
    const cf = p.creativeFont;
    const body = [
      `• Named Creative Reference: ${cf.name}`,
      `• Category: ${cf.cat}`,
      cf.tags && cf.tags.length ? `• Tags: ${cf.tags.join(', ')}` : '',
      cf.era ? `• Era: ${cf.era}` : '',
      `• Weight: ${cf.weight} · Width: ${cf.width} · Shape: ${cf.shape} · Baseline: ${cf.baseline} · Capitalization: ${cf.cap}`,
      cf.readability ? `• Readability: ${cf.readability}` : '',
      cf.materials && cf.materials.length ? `• Recommended Materials: ${cf.materials.join(', ')}` : '',
      cf.effects && cf.effects.length ? `• Recommended Effects: ${cf.effects.join(', ')}` : '',
      cf.pairings && cf.pairings.length ? `• Recommended Pairings: ${cf.pairings.join(', ')}` : '',
      `• Visual Description: ${cf.description}.`,
      `• Trademark-safe Fallback: ${cf.fallback}.`,
    ].filter(Boolean).join('\n');
    out.push(block('=== CREATIVE / CHARACTER-INSPIRED REFERENCE ===', body), '');
  }

  // C2. Custom font entry (unlimited free-form)
  if (p.customFont && Object.values(p.customFont).some(v => v)) {
    const cf = p.customFont;
    const lines = [
      cf.name && `• Custom Font / Reference Name: ${cf.name}`,
      cf.description && `• Custom Visual Description: ${cf.description}`,
      cf.category && `• Category: ${cf.category}`,
      cf.weight && `• Weight: ${cf.weight}`,
      cf.width && `• Width: ${cf.width}`,
      cf.shape && `• Letter Shape: ${cf.shape}`,
      cf.baseline && `• Baseline: ${cf.baseline}`,
      cf.readability && `• Readability: ${cf.readability}`,
      cf.application && `• Intended Application: ${cf.application}`,
      cf.pairing && `• Custom Pairing Direction: ${cf.pairing}`,
    ].filter(Boolean).join('\n');
    if (lines) out.push(block('=== CUSTOM FONT ENTRY (USER-DEFINED) ===', lines + '\n• Preserve the user\'s exact wording. Do not silently substitute a generic font.'), '');
  }

  // C3. Font combo (multi-role)
  if (p.fontCombo && Object.values(p.fontCombo).some(r => r && (r.namedFont || r.creativeFont || r.customName || r.customDescription))) {
    const roleLabels = { hero: 'Hero Font', secondary: 'Secondary Font', scriptAccent: 'Script Accent', smallSupporting: 'Small Supporting Text' };
    const lines = Object.entries(p.fontCombo).filter(([, r]) => r && (r.namedFont || r.creativeFont || r.customName || r.customDescription)).map(([roleKey, r]) => {
      const parts = [];
      if (r.namedFont) parts.push(`named font "${r.namedFont}"`);
      if (r.creativeFont && r.creativeFont.name) parts.push(`creative reference "${r.creativeFont.name}" — ${r.creativeFont.description}`);
      if (r.customName) parts.push(`custom "${r.customName}"`);
      if (r.customDescription) parts.push(`described as: ${r.customDescription}`);
      const scope = r.scope ? ` [scope: ${r.scope}]` : '';
      return `• ${roleLabels[roleKey] || roleKey}${scope}: ${parts.join(' · ')}`;
    }).join('\n');
    out.push(block('=== FONT COMBO ===', lines + '\n• Each role is independent — do not blend one into another. Follow each role\'s scope.'), '');
  }

  // C2. Custom lettering directives
  if (p.lettering && Object.values(p.lettering).some(v => v)) {
    const items = Object.entries(p.lettering).filter(([, v]) => v && v !== '(unset)').map(([k, v]) => `• ${k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}: ${v}`);
    if (items.length) out.push(block('=== CUSTOM LETTERING DIRECTIVES ===', items.join('\n') + '\n• Keep every character distinct and legible unless a decorative overlap is intentional.'), '');
  }

  // C3. Spacing — natural language
  const spacingBits = [
    spacingLanguage(p.tracking, 'tracking'),
    spacingLanguage(p.wordSpacing, 'wordSpacing'),
    spacingLanguage(p.lineSpacing, 'lineSpacing'),
    spacingLanguage(p.stackGap, 'stackGap'),
    p.controlledOverlap ? 'intentional controlled letter overlap while keeping letters distinct and readable' : '',
  ].filter(Boolean);
  if (spacingBits.length) {
    out.push(block('=== SPACING & RHYTHM ===',
      `Use ${spacingBits.join(', ')}. Do not promise exact numerical tracking — describe intent visually.`), '');
  }

  // D. FILL & MATERIAL — governed by Fill Mode.
  const mode = p.fillMode || 'Solid Color';
  const fillLines = [`• Fill Mode: ${mode}`];
  if (mode === 'Solid Color') {
    fillLines.push(`• Solid Fill: ${p.solidColor || '#FF107A'}${p.solidOpacity != null && p.solidOpacity !== 100 ? ` at ${p.solidOpacity}% opacity` : ''}`);
    fillLines.push('• Do not describe or render a gradient for this fill — use a single solid color.');
  } else if (mode === 'Custom Gradient' || mode === 'Preset Gradient') {
    const gd = gradientDescription(p.gradient);
    if (gd) fillLines.push(`• Gradient (${mode}): ${gd}`);
    else fillLines.push('• Gradient mode selected but no stops defined — falling back to palette or solid color.');
  } else if (mode === 'Pattern / Material') {
    fillLines.push('• Fill is driven by the selected pattern and/or material below.');
  }
  if (p.palette) fillLines.push(`• Palette Reference: ${palettePretty(p.palette)}`);
  if (p.customPalette && p.customPalette.length) fillLines.push(`• Custom Palette Swatches: ${p.customPalette.join(', ')}`);
  if (p.patternFamily || p.patternSubtype) fillLines.push(`• Pattern: ${[p.patternFamily, p.patternSubtype].filter(Boolean).join(' → ')}`);
  if (p.patternPlacement && p.patternPlacement.length) fillLines.push(`• Pattern Placement: ${p.patternPlacement.join(', ')}`);
  if (p.materialFamily || p.materialSubtype) fillLines.push(`• Material: ${[p.materialFamily, p.materialSubtype].filter(Boolean).join(' → ')}`);
  if (p.materialPlacement && p.materialPlacement.length) fillLines.push(`• Material Placement: ${p.materialPlacement.join(', ')}`);
  if (p.finish) fillLines.push(`• Finish: ${p.finish}`);
  out.push(block('=== FILL & MATERIAL ===', fillLines.join('\n')), '');

  // D2. Per-target fills
  if (p.targetFills && Object.keys(p.targetFills).length) {
    const lines = Object.entries(p.targetFills).map(([t, entry]) => buildFillDescription(p, t, entry || {}));
    out.push(block('=== TARGET-SPECIFIC FILLS ===', lines.join('\n') + '\n• Each target above is independent — do not let one target overwrite another.'), '');
  }

  // E. Layer stack + internal details
  const layerBody = [];
  if (p.layers && p.layers.length) p.layers.forEach((l) => {
    const isOutline = /^Outline\s*\d*$/i.test(l.position || '');
    if (isOutline && l.fillType === 'Gradient' && l.gradient && (l.gradient.stops || []).length >= 2) {
      const g = l.gradient;
      const type = (g.type || 'Linear').toLowerCase();
      const angle = type === 'linear' ? ` at ${g.angle ?? 90}°` : '';
      const stops = (g.stops || []).slice().sort((a,b) => (a.pos||0) - (b.pos||0))
        .map(s => `${s.color || '#000'} at ${Math.round(s.pos ?? 0)}%${(s.opacity != null && s.opacity !== 100) ? ` (${s.opacity}% opacity)` : ''}`)
        .join(', ');
      layerBody.push(`• ${l.position}: ${g.type || 'Linear'} gradient${angle} with stops ${stops}. Apply this gradient only to ${l.position}. Keep the Main Text Fill unchanged. Intensity ${l.intensity ?? 50}%.`);
    } else {
      layerBody.push(`• ${l.position}: color ${l.color || 'default'}, intensity ${l.intensity ?? 50}%`);
    }
  });
  if (p.internalDetails && p.internalDetails.length) layerBody.push(bullet('Internal Letter Details', p.internalDetails));
  if (layerBody.length) out.push(block('=== LAYER STACK & INTERNAL DETAILS ===', layerBody.join('\n')), '');

  // F. Per-letter overrides (individual > word > global precedence)
  if (p.perLetterOverrides && p.perLetterOverrides.length) {
    const body = p.perLetterOverrides.map((o) => {
      const label = o.label || (typeof o.target === 'string' ? `"${o.target}"` : 'override');
      const fields = Object.entries(o.overrides || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join('; ') || 'inherit from word/global';
      return `• ${label} — ${fields}`;
    }).join('\n');
    out.push(block('=== PER-LETTER OVERRIDES ===', body + '\n• Precedence: individual occurrence > word > global. Do not merge repeated letters — each targeted occurrence keeps its own styling.'), '');
  }

  // G. Supporting elements
  if (p.supporting && p.supporting.length) {
    const body = p.supporting.map((s) => (typeof s === 'string' ? `• ${s}` : `• ${s.item} (${s.placement || 'around'}, qty ${s.qty || 1})`)).join('\n');
    out.push(block('=== SUPPORTING ELEMENTS ===', body), 'Support the typography without hiding the exact wording.', '');
  }

  // Holiday treatment (if set) — append narrative
  if (p.customEntries && p.customEntries.holidayTreatment) {
    out.push(block('=== HOLIDAY / SEASONAL TREATMENT ===', `• ${p.customEntries.holidayTreatment}`), '');
  }

  // H. Intensity levers
  if (p.intensity && Object.keys(p.intensity).length) {
    const body = Object.entries(p.intensity).map(([k, v]) => `• ${k}: ${v}%`).join('\n');
    out.push(block('=== PROMPT STRENGTH ===', body), '');
  }

  // I. Lighting / camera / background
  const light = [
    bullet('Lighting', p.lighting),
    bullet('Camera / View', p.camera),
    bullet('Aspect Ratio', p.aspect),
    bullet('Realism Level', p.realism),
    bullet('Composition', p.composition),
    bullet('Text Hierarchy', p.hierarchy),
  ].filter(Boolean).join('\n');
  if (light) out.push(block('=== LIGHTING, CAMERA & COMPOSITION ===', light), '');

  // J. Background / export
  const bg = [bullet('Background Mode', p.backgroundLabel || p.background) || '• Background Mode: not specified — default to a clean isolated presentation'];
  if (isTransparent(p.background)) {
    bg.push('• Deliver one centered, isolated, production-ready design with a truly transparent alpha background when supported.');
    bg.push('• No white box, no colored backdrop, no room, no scenery, no garment, no person wearing it, no product mockup, no display board, no frame around the canvas, no floor shadow outside the artwork.');
    bg.push('• No cropped letters or supporting elements. Clean anti-aliased edges. Keep generous transparent padding.');
    bg.push('• Preserve intentional shadows, glows, drips that belong to the design itself. Request PNG output with true alpha channel.');
  }
  if (p.background === 'isolated-white') bg.push('• Use an isolated pure-white background for easy removal (no gradients, no textures).');
  if (p.background === 'product-mockup' && p.mockupProduct) bg.push(`• Present the design on: ${p.mockupProduct}.`);
  if (p.customBackground) bg.push(`• Custom background instruction: ${p.customBackground}`);
  if (p.canvasWidth && p.canvasHeight) bg.push(`• Canvas: ${p.canvasWidth}×${p.canvasHeight} ${p.canvasUnit || 'px'}, high-resolution intent.`);
  out.push(block('=== BACKGROUND / EXPORT ===', bg.filter(Boolean).join('\n')), '');

  // K. Custom typed instructions (except holidayTreatment already used above)
  if (p.customEntries) {
    const items = Object.entries(p.customEntries).filter(([k, v]) => v && String(v).trim() && k !== 'holidayTreatment');
    if (items.length) out.push(block('=== CUSTOM INSTRUCTIONS ===', items.map(([k, v]) => `• ${k}: ${v}`).join('\n')), '');
  }

  // L. Negatives
  const negs = [...NEGATIVE_INSTRUCTIONS_DEFAULT, ...(p.negatives || [])];
  out.push(block('=== NEGATIVE / QUALITY ===', negs.map((n) => `• ${n}`).join('\n')), '');

  // M. Final lock
  out.push('=== FINAL LOCK ===');
  out.push(`Render the exact readable text: "${p.exactText || ''}". Preserve every letter in the correct order. No extra letters, missing letters, substitutions, duplicated characters, watermark, signature, or unrelated text. Keep the typography as the visual hero.`);
  out.push('Font precedence: individual character occurrence > selected letter group > word > line > global font. Never merge repeated letters automatically.');
  out.push('Treat named fonts and creative references as visual direction. If an exact typeface is unavailable, recreate its recognizable construction, proportion, rhythm, personality and readability without replacing it with generic lettering.');

  return out.filter((v) => v !== undefined).join('\n');
}

export function buildVariations(p) {
  const base = compileMasterPrompt(p);
  const twists = [
    { name: 'Tighter Crop', mod: '\n\nVARIATION: Present with a tighter crop that emphasizes the exact text as the hero. Reduce background presence, increase typography scale by ~20%.' },
    { name: 'Rich Depth', mod: '\n\nVARIATION: Push dimensional depth and material realism further. Deepen bevels, sharpen highlights, add a subtle rim light along top edges.' },
    { name: 'Editorial Minimal', mod: '\n\nVARIATION: Editorial minimal treatment — reduce supporting elements to 30%, expand negative space, calmer color grading, simpler layer stack.' },
    { name: 'Punchy Contrast', mod: '\n\nVARIATION: Punchy high-contrast palette, tighter shadows, stronger separation between face and background, more saturated highlights.' },
    { name: 'Cinematic Mood', mod: '\n\nVARIATION: Cinematic lighting with volumetric atmosphere, moody shadow falloff, slight film grain, dramatic key light from upper-left.' },
  ];
  return twists.map((t) => ({ name: t.name, prompt: base + t.mod }));
}

// Alphabet / character-sheet prompt: distinct output when enabled.
export function buildCharacterSheetPrompt(p) {
  const cs = p.characterSheet || {};
  if (!cs.enabled || !(cs.chars || '').trim()) return '';
  const chars = cs.chars.trim();
  // Split long sets into ~24-char chunks so a single image sheet can render each cleanly.
  const chunks = [];
  const raw = chars.split('');
  let cur = '';
  for (const ch of raw) {
    cur += ch;
    if (cur.replace(/\s/g,'').length >= 26) { chunks.push(cur); cur = ''; }
  }
  if (cur.trim()) chunks.push(cur);
  const styleSummary = [
    p.fontStyle && `direction: ${p.fontStyle}`,
    p.materialFamily && `material: ${[p.materialFamily, p.materialSubtype].filter(Boolean).join(' → ')}`,
    p.finish && `finish: ${p.finish}`,
    p.palette && `palette: ${palettePretty(p.palette)}`,
  ].filter(Boolean).join(' · ');
  return chunks.map((chunk, i) => (
`=== ALPHABET / CHARACTER SHEET PROMPT${chunks.length > 1 ? ` (${i + 1}/${chunks.length})` : ''} ===
Render this exact set of characters as a coordinated LETTERING ARTWORK SHEET (not an installable OTF/TTF font):
CHARACTERS: ${chunk}
• Every character shares the same design language${styleSummary ? ` — ${styleSummary}` : ''}.
• Arrange the characters on a clean grid with even spacing so each glyph is fully visible.
• Preserve custom lettering directives, spacing intent, and any per-letter overrides from the main recipe.
• Keep letters distinct and legible; no missing or duplicated characters.
• Deliver as a single high-resolution artwork (transparent background when supported).`
  )).join('\n\n');
}

export function buildRecipe(p) {
  const entries = [];
  const push = (k, v) => { if (v && (Array.isArray(v) ? v.length : true)) entries.push({ k, v: Array.isArray(v) ? v.join(', ') : String(v) }); };
  push('Text', p.exactText);
  push('Creation Type', p.creationTypeLabel || p.creationType);
  push('Topic', [p.topicCategoryLabel, p.topicItem, p.topicCustom].filter(Boolean).join(' — '));
  push('Font Family', p.fontFamilyLabel || p.fontFamily);
  push('Font Direction', p.fontStyle);
  push('Named Font', p.namedFont);
  if (p.creativeFont && p.creativeFont.name) push('Creative Reference', p.creativeFont.name);
  if (p.customFont && p.customFont.name) push('Custom Font', p.customFont.name);
  if (p.fontCombo) {
    const roles = Object.entries(p.fontCombo).filter(([, r]) => r && (r.namedFont || r.creativeFont || r.customName));
    if (roles.length) push('Font Combo Roles', roles.map(([k]) => k).join(', '));
  }
  push('Construction', p.letterConstruction);
  push('Fill Mode', p.fillMode);
  if (p.fillMode === 'Solid Color') push('Solid Color', p.solidColor);
  if (p.fillMode === 'Custom Gradient' || p.fillMode === 'Preset Gradient') {
    if (p.gradient && p.gradient.stops && p.gradient.stops.length >= 2) push('Gradient', `${p.gradient.type} @ ${p.gradient.angle}° (${p.gradient.stops.length} stops)`);
  }
  push('Material', [p.materialFamily, p.materialSubtype].filter(Boolean).join(' → '));
  push('Material Placement', p.materialPlacement);
  push('Pattern', [p.patternFamily, p.patternSubtype].filter(Boolean).join(' → '));
  push('Pattern Placement', p.patternPlacement);
  push('Finish', p.finish);
  push('Palette', p.palette);
  push('Custom Swatches', p.customPalette);
  push('Tracking', p.tracking ? spacingLanguage(p.tracking, 'tracking') : '');
  push('Line Spacing', p.lineSpacing ? spacingLanguage(p.lineSpacing, 'lineSpacing') : '');
  push('Word Spacing', p.wordSpacing ? spacingLanguage(p.wordSpacing, 'wordSpacing') : '');
  push('Stack Gap', p.stackGap ? spacingLanguage(p.stackGap, 'stackGap') : '');
  if (p.controlledOverlap) push('Overlap', 'controlled intentional');
  if (p.characterSheet && p.characterSheet.enabled && p.characterSheet.chars) push('Character Sheet', `${p.characterSheet.chars.length} chars`);
  if (p.targetFills && Object.keys(p.targetFills).length) push('Per-Target Fills', Object.keys(p.targetFills));
  push('Supporting', p.supporting);
  push('Background', p.backgroundLabel || p.background);
  return entries;
}

export function buildMockupPrompt(masterPrompt, product) {
  return `${masterPrompt}\n\n=== PRODUCT MOCKUP OVERLAY ===\nTake the design above and present it as if applied to: ${product || 'a plain t-shirt'}.\nShow the product from a natural three-quarter angle with soft studio lighting. Preserve the design details, wording, colors, and finish exactly. Do not distort the artwork. Keep any transparent regions correctly cut. This is a separate MOCKUP PROMPT — the transparent master prompt above remains unchanged.`;
}

// Compatibility rules: return warnings but never mutate state.
export function detectConflicts(p) {
  const warnings = [];
  if (p.materialFamily === 'Liquid / Gel' && p.finish === 'Matte') warnings.push('Liquid/Gel materials rarely appear matte. Consider Glossy or Wet.');
  if (p.materialFamily === 'Metal' && p.materialSubtype && p.materialSubtype.includes('Chrome') && p.finish === 'Matte') warnings.push('Chrome + Matte conflict. Suggest Mirrored, Ultra-Glossy or Brushed.');
  if (p.background === 'transparent-typo' && (p.supporting || []).length > 6) warnings.push('Transparent typography-only with many supporting elements may not fit inside the canvas. Consider Transparent Typography + Supporting Elements instead.');
  if (p.letterConstruction === 'Chenille Patch' && p.finish === 'Ultra-Glossy') warnings.push('Chenille is a matte woven texture; Ultra-Glossy will look artificial. Consider Fuzzy or Matte.');
  if (p.fillMode === 'Solid Color' && p.gradient && p.gradient.stops && p.gradient.stops.length >= 2) warnings.push('Fill Mode is Solid Color but a gradient is also defined. The compiler will emit only the solid color and ignore the gradient.');
  return warnings;
}
